import React from 'react';
import { useNode } from '@craftjs/core';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';
import { useTranslation } from '@/i18n/hooks/useTranslation';

interface HeroCarSearchProps {
  title?: string;
  subtitle?: string;
  backgroundImage?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  textColor?: string;
  searchButtonColor?: string;
  searchBoxBackground?: string;
  searchBoxTextColor?: string;
  showMakeModel?: boolean;
  showPrice?: boolean;
  showYear?: boolean;
  buttonText?: string;
}

export const HeroCarSearch = ({
  title = 'Encuentra tu auto ideal',
  subtitle = 'Busca entre miles de vehículos disponibles',
  backgroundImage = 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1470',
  overlayColor = '#000000',
  overlayOpacity = 0.5,
  textColor = '#ffffff',
  searchButtonColor = '#3b82f6',
  searchBoxBackground = '#ffffff',
  searchBoxTextColor = '#333333',
  showMakeModel = true,
  showPrice = true,
  showYear = true,
  buttonText = 'Buscar',
}: HeroCarSearchProps) => {
  const { t } = useTranslation();
  const { connectors, selected } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // Los tres campos son opcionales. En un buscador no corresponde centrarlos
  // dejando aire a los costados: los que estén visibles se reparten TODO el
  // ancho (`flex-1`) y, si no entran, bajan y se estiran igual. Así nunca
  // queda el hueco de una columna vacía ni un campo huérfano al medio.
  const fieldClass = 'flex-1 basis-[220px] min-w-0';

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        color: textColor,
        border: selected ? '1px dashed #1e88e5' : '1px solid transparent',
      }}
      className='w-full min-h-[500px] flex items-center'
    >
      <div style={{ backgroundColor: overlayColor, opacity: overlayOpacity }} className='absolute inset-0 z-0' />
      <AreaDePiezas className='container mx-auto px-4 z-10 relative py-12'>
        <div className='max-w-3xl mx-auto text-center mb-8'>
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            <h1 style={{ color: textColor }} className='text-4xl md:text-5xl font-bold mb-4'>{title}</h1>
          </Pieza>
          <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            <p style={{ color: textColor }} className='text-lg md:text-xl'>{subtitle}</p>
          </Pieza>
        </div>
        <Pieza clave="buscador" tipo="coleccion" etiqueta="Buscador">
          <div style={{ backgroundColor: searchBoxBackground, color: searchBoxTextColor }} className='rounded-lg shadow-lg p-4 md:p-6 max-w-4xl mx-auto'>
            <div className='flex flex-wrap gap-4'>
              {showMakeModel && (
                <div className={fieldClass}>
                  <label className='text-sm font-medium mb-1 block'>{t('site.hero.makeModel')}</label>
                  <input type='text' placeholder={t('site.hero.makeModelPlaceholder')} className='w-full p-2 border rounded' />
                </div>
              )}
              {showYear && (
                <div className={fieldClass}>
                  <label className='text-sm font-medium mb-1 block'>{t('site.vehicles.year')}</label>
                  <div className='grid grid-cols-2 gap-2'>
                    <input type='number' placeholder={t('site.hero.from')} className='w-full p-2 border rounded' />
                    <input type='number' placeholder={t('site.hero.to')} className='w-full p-2 border rounded' />
                  </div>
                </div>
              )}
              {showPrice && (
                <div className={fieldClass}>
                  <label className='text-sm font-medium mb-1 block'>{t('site.hero.price')}</label>
                  <div className='grid grid-cols-2 gap-2'>
                    <input type='number' placeholder={t('site.hero.min')} className='w-full p-2 border rounded' />
                    <input type='number' placeholder={t('site.hero.max')} className='w-full p-2 border rounded' />
                  </div>
                </div>
              )}
            </div>
            <div className='mt-6 text-center'>
              <button style={{ backgroundColor: searchButtonColor }} className='px-6 py-2 text-white rounded-md hover:opacity-90 transition-opacity'>
                {buttonText}
              </button>
            </div>
          </div>
        </Pieza>
      </AreaDePiezas>
    </div>
  );
};

HeroCarSearch.craft = {
  displayName: 'Hero con Buscador',
  props: {
    title: 'Encuentra tu auto ideal',
    subtitle: 'Busca entre miles de vehículos disponibles',
    backgroundImage: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1470',
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    textColor: '#ffffff',
    searchButtonColor: '#3b82f6',
    searchBoxBackground: '#ffffff',
    searchBoxTextColor: '#333333',
    showMakeModel: true,
    showPrice: true,
    showYear: true,
    buttonText: 'Buscar',
  },
};
